import React, { useState } from "react";
import { Card, Stack } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faCheck, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { NeedsHousingRow, pluralizePeople } from "../../helpers/housingAssignment";

interface NeedsHousingPanelState {
  rows: NeedsHousingRow[];
  onFindBed: (row: NeedsHousingRow) => void;
}

const countLabel = (row: NeedsHousingRow) =>
  row.needCount < row.size ? `${row.needCount} of ${row.size} people` : pluralizePeople(row.size);

const placedLabel = (row: NeedsHousingRow) => {
  if (row.placedRoomCount > 1) return "Rest of case in multiple locations";
  if (row.placedRoomLabel) return `Rest of the case is in ${row.placedRoomLabel}`;
  return null;
};

const NeedsHousingPanel = ({ rows, onFindBed }: NeedsHousingPanelState) => {
  const [expanded, setExpanded] = useState(false);

  const peopleCount = rows.reduce((n, r) => n + r.needCount, 0);

  const onClickFindBed = (row: NeedsHousingRow) => {
    setExpanded(false);
    onFindBed(row);
  };

  if (rows.length === 0) {
    return (
      <Card className="bg-light mb-4">
        <Stack direction="horizontal" gap={3} className="p-2 align-items-center">
          <FontAwesomeIcon icon={faCheck} style={{ color: "#7d9158" }} />
          <span className="small fw-semibold">Everyone has housing</span>
          <span className="small text-muted">Nothing left to place</span>
        </Stack>
      </Card>
    );
  }

  return (
    <Card className="bg-light mb-4">
      <Stack direction="horizontal" gap={2} className="p-2 align-items-center cursor-pointer" onClick={() => setExpanded((v) => !v)}>
        <span className="role-dot role-dot-orange"></span>
        <span className="small fw-semibold">{peopleCount === 1 ? "1 person needs housing" : `${peopleCount} people need housing`}</span>
        <span className="small text-muted">{rows.length === 1 ? "in 1 case" : `in ${rows.length} cases`}</span>
        <Stack direction="horizontal" gap={1} className="ms-auto align-items-center small text-muted">
          <span>{expanded ? "Hide" : "Show"}</span>
          <FontAwesomeIcon
            icon={faChevronDown}
            fontSize={12}
            style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.18s ease" }}
          />
        </Stack>
      </Stack>

      {expanded && (
        <div className="border-top">
          {rows.map((row: NeedsHousingRow) => (
            <Stack
              key={row.caseId}
              direction="horizontal"
              gap={3}
              className="px-2 py-2 align-items-start cursor-pointer"
              onClick={() => onClickFindBed(row)}
            >
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div>
                  <span className="small fw-semibold">{row.caseName}</span>
                  <span className="small text-muted ms-1">{countLabel(row)}</span>
                </div>
                {placedLabel(row) && <div className="small text-muted">{placedLabel(row)}</div>}
              </div>
              {/* the whole row carries the click - this only stops the anchor navigating */}
              <a href="#blank" className="quick-link-on-card small flex-shrink-0" onClick={(e: any) => e.preventDefault()}>
                Find a bed
                <FontAwesomeIcon icon={faArrowDown} fontSize={11} className="ms-1" />
              </a>
            </Stack>
          ))}
        </div>
      )}
    </Card>
  );
};

export default NeedsHousingPanel;
